import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PhoneInput } from '@/components/ui/PhoneInput'
import { Country, findCountryByPhoneCode, countries } from '@/data/countries'
import { userApi } from '@/services/api'
import { User } from '@/types'
import { useDebounce } from '@/hooks/useDebounce'
import { Loader2, User as UserIcon, X } from 'lucide-react'

interface ManualContactFormProps {
  name: string
  phoneNumber: string
  email: string
  selectedCountry: Country | null
  isSaving: boolean
  onNameChange: (value: string) => void
  onPhoneChange: (fullNumber: string, country: Country) => void
  onEmailChange: (value: string) => void
  onSave: () => void
}

export function ManualContactForm({
  name,
  phoneNumber,
  email,
  selectedCountry,
  isSaving,
  onNameChange,
  onPhoneChange,
  onEmailChange,
  onSave
}: ManualContactFormProps) {
  const { t } = useTranslation()
  const [suggestedUsers, setSuggestedUsers] = useState<User[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  // Build search query from email or phone (suggestions appear under email field)
  // We'll search users when either email or phone is provided
  const phoneWithCode = selectedCountry && phoneNumber 
    ? selectedCountry.phoneCode + phoneNumber.replace(/\s/g, '') 
    : phoneNumber.replace(/\s/g, '')
  const searchQueryEmail = email.trim()
  const searchQuery = searchQueryEmail || phoneWithCode || ''
  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  // Search users when debounced query changes
  useEffect(() => {
    const searchUsers = async () => {
      if (!debouncedSearchQuery || debouncedSearchQuery.length < 2) {
        setSuggestedUsers([])
        setShowSuggestions(false)
        return
      }

      setIsSearching(true)
      try {
        const response = await userApi.searchUsers(debouncedSearchQuery)
        const users = response.data.data || []
        setSuggestedUsers(users)
        // Always show suggestions area when we have a search query (to show "no user found" message)
        setShowSuggestions(debouncedSearchQuery.length >= 2)
      } catch (error) {
        console.error('Error searching users:', error)
        setSuggestedUsers([])
        // Still show suggestions area to display "no user found" message
        setShowSuggestions(debouncedSearchQuery.length >= 2)
      } finally {
        setIsSearching(false)
      }
    }

    // Search when email OR phone is provided (at least 2 characters)
    if (debouncedSearchQuery.length >= 2) {
      searchUsers()
    } else {
      setSuggestedUsers([])
      setShowSuggestions(false)
    }
  }, [debouncedSearchQuery])

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelectUser = (user: User) => {
    setSelectedUser(user)
    setShowSuggestions(false)
    
    // Fill form with user data
    if (user.email) {
      onEmailChange(user.email)
    }
    if (user.phone_number) {
      // Extract country code if possible
      const phoneWithCode = user.phone_number
      // Try to find country by phone code
      const defaultCountry = findCountryByPhoneCode('+239') || countries[0]
      onPhoneChange(phoneWithCode, selectedCountry || defaultCountry)
    }
    if (user.profile?.first_name || user.profile?.last_name) {
      const fullName = `${user.profile.first_name || ''} ${user.profile.last_name || ''}`.trim()
      if (fullName) {
        onNameChange(fullName)
      }
    }
  }

  const handleClearSelection = () => {
    setSelectedUser(null)
    onEmailChange('')
    const defaultCountry = findCountryByPhoneCode('+239') || countries[0]
    onPhoneChange('', selectedCountry || defaultCountry)
    onNameChange('')
  }

  // Valid if name is provided and at least phone OR email is provided
  const trimmedName = (name || '').toString().trim()
  const trimmedPhone = (phoneNumber || '').toString().trim()
  const trimmedEmail = (email || '').toString().trim()
  
  const isValid = 
    trimmedName && 
    (trimmedPhone || trimmedEmail) && 
    (trimmedPhone ? !!selectedCountry : true) // Country required only if phone is provided

  return (
    <div className="space-y-4">
      <p className="text-sm text-text-secondary mb-4">
        {t('contacts.fillAtLeastOne', 'Preencha pelo menos telefone ou email')}
      </p>

      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          {t('contacts.name', 'Nome')} *
        </label>
        <Input
          type="text"
          placeholder={t('contacts.enterName', 'Introduza o nome')}
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onNameChange(e.target.value)}
        />
      </div>

      {/* Selected User Display */}
      {selectedUser && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
              <UserIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-text-primary">
                {selectedUser.profile?.first_name && selectedUser.profile?.last_name
                  ? `${selectedUser.profile.first_name} ${selectedUser.profile.last_name}`
                  : selectedUser.email}
              </p>
              <p className="text-xs text-text-secondary">
                {selectedUser.email} {selectedUser.phone_number && `• ${selectedUser.phone_number}`}
              </p>
            </div>
          </div>
          <button
            onClick={handleClearSelection}
            className="p-1 hover:bg-blue-100 dark:hover:bg-blue-800 rounded-full transition-colors"
          >
            <X className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </button>
        </div>
      )}

      {/* Phone */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          {t('contacts.phone', 'Telefone')} {t('contacts.orEmail', '(ou email)')}
        </label>
        <PhoneInput
          value={selectedCountry ? selectedCountry.phoneCode + phoneNumber : phoneNumber}
          onChange={onPhoneChange}
          placeholder={t('contacts.enterPhone', 'Introduza o número')}
        />
      </div>

      {/* Email */}
      <div className="relative" ref={suggestionsRef}>
        <label className="block text-sm font-medium text-text-primary mb-2">
          {t('contacts.email', 'Email')} {t('contacts.orPhone', '(ou telefone)')}
        </label>
        <Input
          type="email"
          placeholder={t('contacts.enterEmail', 'Introduza o email')}
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onEmailChange(e.target.value)}
        />
        
        {/* User Suggestions for Email (appears when searching by email or phone) */}
        {showSuggestions && !selectedUser && debouncedSearchQuery.length >= 2 && (
          <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {isSearching && (
              <div className="p-4 text-center">
                <Loader2 className="w-5 h-5 animate-spin text-primary mx-auto" />
              </div>
            )}
            {!isSearching && suggestedUsers.length > 0 && suggestedUsers.map((user) => (
              <button
                key={user.id}
                onClick={() => handleSelectUser(user)}
                className="w-full px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-3 text-left transition-colors"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <UserIcon className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {user.profile?.first_name && user.profile?.last_name
                      ? `${user.profile.first_name} ${user.profile.last_name}`
                      : user.email}
                  </p>
                  <p className="text-xs text-text-secondary truncate">
                    {user.email} {user.phone_number && `• ${user.phone_number}`}
                  </p>
                </div>
              </button>
            ))}
            {!isSearching && suggestedUsers.length === 0 && debouncedSearchQuery.length >= 2 && (
              <div className="p-4 text-center">
                <p className="text-sm text-text-secondary">
                  {t('contacts.noUserFound', 'Nenhum utilizador encontrado com este número ou email')}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Save Button */}
      <Button
        variant="primary"
        className="w-full"
        onClick={onSave}
        disabled={isSaving || !isValid}
      >
        {isSaving ? t('common.saving', 'A guardar...') : t('common.save', 'Guardar')}
      </Button>
    </div>
  )
}

